import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import Card from "../../components/Card/Card";
import useAuth from "../../hooks/useAuth";
import { submitBusinessPlanSchema } from "../../services/SchemaService";
import { Button, Form, Loading, Tabs, Tab } from "carbon-components-react";
import SubmitButton from "../../components/Buttons/SubmitButton";
import CancelButton from "../../components/Buttons/CancelButton";
import { ButtonSet } from "carbon-components-react";
import { Edit24 } from "@carbon/icons-react";
import { SendAlt24 } from "@carbon/icons-react";
import { ArrowRight20 } from "@carbon/icons-react";
import { ArrowLeft20 } from "@carbon/icons-react";
import http from "../../http-common";
import { toast } from "react-toastify";
import useGet from "../../hooks/useGet";
import useBusinessPlan from "../../hooks/useBusinessPlan";
import MarketAnalysisForm from "../../components/Forms/MarketAnalysisForm";
import WorkersForm from "../../components/Forms/WorkersForm";
import FinancialPlanForm from "../../components/Forms/FinancialPlanForm";
import EnvironmentalSocialAnalysisForm from "../../components/Forms/EnvironmentalSocialAnalysisForm";
import DataSheetForm from "../../components/Forms/DataSheetForm";
import ExistingBusinessProfileForm from "../../components/Forms/ExistingBusinessProfileForm";

const ViewBusinessPlanPage = () => {
  const params = useParams();
  const [uuid] = useState(params.uuid);
  const { businessPlan, isLoading: businessPlanLoading } =
    useBusinessPlan(uuid);
  const [hasBusinessCurrently, setHasBusinessCurrently] = useState(null);
  const { data: legalForms } = useGet("/lookup/legal-forms");
  const [isOpen, setIsOpen] = useState(false);
  const { user, isCompetitor } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isReopening, setIsReopening] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const isAdmin = user && String(user.roleCode) === "200";

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    control,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(submitBusinessPlanSchema),
  });

  const onSubmit = async (data) => {
    try {
      const response = await http.post("/business-plan/submit/" + uuid);

      toast.success(
        "Plano de Negócio submetido com sucesso. Obrigado e boa sorte!"
      );
      setIsOpen(response.data.currentStatusCode === 1);
      //setIsOpen(false);
    } catch (error) {
      toast.error(error?.response?.data.message);
    }
  };

  const handleValidationError = () => {
    toast.error(
      "Por favor, reveja o formulário e garanta que todos os campos obrigatórios estão preenchidos."
    );
  };

  const handleReopen = async () => {
    if (!uuid) {
      return;
    }
    try {
      setIsReopening(true);
      setIsLoading(true);
      const response = await http.post(`/business-plan/open/${uuid}`);
      const updatedPlan = response?.data;
      if (updatedPlan) {
        reset(updatedPlan);
        setHasBusinessCurrently(updatedPlan.hasBusinessCurrently);
        setIsOpen(updatedPlan.currentStatusCode === 1);
      } else {
        setIsOpen(true);
      }
      toast.success("Plano de Negócio reaberto com sucesso.");
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Não foi possível reabrir o Plano de Negócio."
      );
    } finally {
      setIsReopening(false);
      setTimeout(() => {
        setIsLoading(false);
      }, 0);
    }
  };

  useEffect(() => {
    if (
      !businessPlanLoading &&
      businessPlan &&
      Object.keys(businessPlan).length > 0
    ) {
      reset(businessPlan);
      setIsOpen(businessPlan.currentStatusCode === 1);
      setHasBusinessCurrently(businessPlan.hasBusinessCurrently);
      setTimeout(() => {
        setIsLoading(false);
      }, "2000");
    }
  }, [uuid, businessPlan, businessPlanLoading, reset]);

  const nextTab = () => {
    setActiveTab((prev) => prev + 1);
  };
  const previousTab = () => {
    setActiveTab((prev) => prev - 1);
  };

  return (
    <>
      <Loading
        active={isLoading}
        description="Active loading indicator"
        withOverlay={true}
      />
      <Card title="Plano de Negócio">
        <Form onSubmit={handleSubmit(onSubmit, handleValidationError)}>
          <Tabs
            scrollIntoView={false}
            selected={activeTab}
            type={"container"}
            onSelectionChange={(e) => setActiveTab(e)}
          >
            <Tab href="#" id="tab-1" label="SECÇÃO 1">
              <DataSheetForm
                register={register}
                control={control}
                errors={errors}
                disabled={true}
                businessPlan={businessPlan}
                hasBusinessCurrently={hasBusinessCurrently}
                setHasBusinessCurrently={setHasBusinessCurrently}
                setValue={setValue}
                getValues={getValues}
              />
              <div className="tab-navigation">
                <Button kind="ghost" size="sm" onClick={nextTab}>
                  Próximo <ArrowRight20 />
                </Button>
              </div>
            </Tab>

            <Tab href="#" id="tab-2" label="SECÇÃO 2">
              <ExistingBusinessProfileForm
                register={register}
                control={control}
                errors={errors}
                setHasBusinessCurrently={setHasBusinessCurrently}
                disabled={true}
                businessPlan={businessPlan}
              />
              <div className="tab-navigation">
                <Button kind="ghost" size="sm" onClick={previousTab}>
                  <ArrowLeft20 /> Anterior
                </Button>
                <Button kind="ghost" size="sm" onClick={nextTab}>
                  Próximo <ArrowRight20 />
                </Button>
              </div>
            </Tab>
            <Tab href="#" id="tab-3" label="SECÇÃO 3">
              <MarketAnalysisForm
                register={register}
                errors={errors}
                disabled={true}
              />
              <div className="tab-navigation">
                <Button kind="ghost" size="sm" onClick={previousTab}>
                  <ArrowLeft20 /> Anterior
                </Button>
                <Button kind="ghost" size="sm" onClick={nextTab}>
                  Próximo <ArrowRight20 />
                </Button>
              </div>
            </Tab>
            <Tab href="#" id="tab-4" label="SECÇÃO 4">
              <WorkersForm
                register={register}
                errors={errors}
                disabled={true}
                control={control}
                getValues={getValues}
                businessPlan={businessPlan}
              />
              <div className="tab-navigation">
                <Button kind="ghost" size="sm" onClick={previousTab}>
                  <ArrowLeft20 /> Anterior
                </Button>
                <Button kind="ghost" size="sm" onClick={nextTab}>
                  Próximo <ArrowRight20 />
                </Button>
              </div>
            </Tab>
            <Tab href="#" id="tab-5" label="SECÇÃO 5">
              <FinancialPlanForm
                register={register}
                control={control}
                errors={errors}
                disabled={true}
                businessPlan={businessPlan}
                getValues={getValues}
                setValue={setValue}
              />
              <div className="tab-navigation">
                <Button kind="ghost" size="sm" onClick={previousTab}>
                  <ArrowLeft20 /> Anterior
                </Button>
                <Button kind="ghost" size="sm" onClick={nextTab}>
                  Próximo <ArrowRight20 />
                </Button>
              </div>
            </Tab>
            <Tab href="#" id="tab-6" label="SECÇÃO 6">
              <EnvironmentalSocialAnalysisForm
                businessPlan={businessPlan}
                register={register}
                errors={errors}
                disabled={true}
              />
              <div className="tab-navigation">
                <Button kind="ghost" size="sm" onClick={previousTab}>
                  <ArrowLeft20 /> Anterior
                </Button>
              </div>
            </Tab>
          </Tabs>
          <div className="form-buttons">
            <ButtonSet>
              {isOpen && (
                <Button
                  renderIcon={Edit24}
                  iconDescription="Editar"
                  onClick={() =>
                    navigate("../editar-plano-de-negocio/" + businessPlan.uuid)
                  }
                >
                  Editar
                </Button>
              )}
              {isAdmin && !isOpen && (
                <Button
                  kind="primary"
                  onClick={handleReopen}
                  disabled={isReopening}
                >
                  {isReopening ? "A Reabrir..." : "Reabrir Plano"}
                </Button>
              )}
              <CancelButton
                label={"Voltar"}
                navigateTo={
                  isCompetitor
                    ? isOpen
                      ? "../editar-plano-de-negocio/" + businessPlan.uuid
                      : "../inicio"
                    : ""
                }
              />
            </ButtonSet>
            {isOpen && isCompetitor && (
              <ButtonSet>
                <SubmitButton
                  label="Submeter"
                  icon={SendAlt24}
                  className="emprega-bg-orange"
                />
              </ButtonSet>
            )}
          </div>
        </Form>
      </Card>
    </>
  );
};

export default ViewBusinessPlanPage;
